import { Component, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';
import Utils from 'src/app/helpers/utils';

@Component({
  selector: 'app-dashboard-permissions-list',
  templateUrl: './dashboard-permissions-list.component.html',
  styleUrls: ['./dashboard-permissions-list.component.scss'],
})
export class DashboardPermissionsListComponent implements OnInit {
  permissionObj = {
    canAdd: true,
    canInactive: true,
    canFilter: true,
  };

  data: PermissionListValues[] = [];

  filterForm = this.formBuilder.group({
    name: [null, []],
    start_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    end_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    status: [null, []],
  });

  statusOptions: SelectItem[] = [
    { label: 'Ativo', value: 'true' },
    { label: 'Inativo', value: 'false' },
  ];

  loading = true;
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];
  showFilterModal = false;
  offset: number = 0;
  count: number = 0;
  results_per_page = 0;

  // Propriedades para o warning modal
  showStatusModal = false;
  selectedPermission: any = null;
  isActive: boolean = false;
  loadingStatus: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private api: ApiService,
    private toast: ToastService,
    public signupService: SignupService,
    private router: Router,
    private authService: AuthService,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.authService.canViewPage(['de_can_view_permissions']);

    this.setBreadcrumb();
    this.setPermissions();
    this.getData();
  }

  async getData(page: number = 1) {
    try {
      const filters = {};

      if (this.hasFilters) {
        const values = this.filterForm.value;
        Object.entries(values).forEach(([key, value]: any) => {
          if (value !== null && value !== '') {
            let auxValue = value;
            if (key === 'start_date' || key === 'end_date') {
              auxValue = value.split('/').reverse().join('-');
            }
            filters[key] = auxValue;
          }
        });
      }

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const { count, results, offset, results_per_page } = await this.api.get({
        route: 'api/v2/group/',
        token: true,
        params: { page: page, type: 'list', ...filters },
      });

      this.offset = offset;
      this.count = count;
      this.data = results;
      this.results_per_page = results_per_page;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  addPermission() {
    this.router.navigateByUrl('/app/permissions/new');
  }

  togglePermissionStatus(id: any) {
    const permission = this.data.find((item) => item.id === id);
    this.selectedPermission = permission;
    this.isActive = permission.status === 'Ativo';
    this.showStatusModal = true;
  }

  async confirmStatusChange() {
    this.loadingStatus = true;
    try {
      await this.api.patch({
        route: `api/v2/group/${this.selectedPermission.id}/`,
        token: true,
        body: { status: !this.isActive, name: this.selectedPermission.group },
      });

      this.loading = true;
      this.showStatusModal = false;
      await this.getData();

      this.toast.show(
        'info',
        'Sucesso',
        `Permissão ${this.isActive ? 'inativada' : 'ativada'} com sucesso.`
      );
    } catch (error) {
      console.error(error);
      this.toast.show(
        'error',
        'Erro',
        `Ocorreu um erro ao alterar status da permissão.`
      );
    } finally {
      this.loadingStatus = false;
    }
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.selectedPermission = null;
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive
      ? 'Inativar grupo de permissão'
      : 'Ativar grupo de permissão';
  }

  getModalMessage(): string {
    return `Deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } esse grupo de permissão? Todos os perfis de usuário vinculados a ela serão afetados nessa operação.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} grupo de permissões`;
  }

  editPermission(id: any) {
    this.router.navigateByUrl(`/app/permissions/update/${id}`);
  }

  redirectToDetails(id: any) {
    this.router.navigateByUrl(`/app/permissions/details/${id}`);
  }

  changePageData(page: number) {
    this.loading = true;
    this.getData(page);
  }

  async refreshData() {
    this.loading = true;
    await this.getData();
  }

  resetForm() {
    this.hasFilters = false;
    this.appliedFilters = [];
    this.filterForm.reset();
    this.loading = true;
    this.getData();
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      name: 'Grupo',
      status: 'Status',
      start_date: 'Data inicial',
      end_date: 'Data final',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        let displayValue = value;

        // Tratamento especial para status
        if (key === 'status') {
          const statusOption = this.statusOptions.find(
            (s) => s.value === value
          );
          displayValue = statusOption ? statusOption.label : value;
        }

        // Tratamento especial para datas (já vem formatado do formulário)
        if (key === 'start_date' || key === 'end_date') {
          displayValue = value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  async filterData() {
    // Verificar se o formulário é válido antes de processar
    if (this.filterForm.invalid) {
      // Marcar todos os campos como touched para mostrar erros
      Object.keys(this.filterForm.controls).forEach((key) => {
        this.filterForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.loading = true;

    try {
      const values = this.filterForm.value;

      const filters = {};

      Object.entries(values).forEach(([key, value]: any) => {
        if (value !== null && value !== '') {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.filterForm.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            filters[key] = newValue;
          }
        }
      });

      if (filters['start_date'] && !filters['end_date']) {
        const atualDate = new Date().toISOString().split('T')[0];
        filters['end_date'] = atualDate;
      }

      if (filters['end_date'] && !filters['start_date']) {
        filters['start_date'] = '2000-01-01';
      }

      const { results, count, offset } = await this.api.get({
        route: 'api/v2/group/',
        token: true,
        params: {
          page: 1,
          type: 'list',
          ...filters,
        },
      });

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(filters).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(values);

      this.count = count;
      this.data = results;
      this.offset = offset;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao carregar os dados.');
    }
    this.loading = false;
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_permissions`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  setBreadcrumb(): void {
    const canViewPermissions = this.authService.verifyPermission(
      'de_can_view_permissions'
    );
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Permissões',
        url: '/app/permissions',
        clickable: canViewPermissions,
      },
      {
        label: 'Controle de permissões',
        url: '/app/permissions/list',
        clickable: false, // Página atual, não precisa ser clicável
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }

  displayDateTime(dateStr: string) {
    if (!dateStr) return { date: '-', time: '' };

    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return {
      date: `${day}/${month}/${year}`,
      time: `às ${hours}:${minutes}`,
    };
  }

  /**
   * Verifica se os filtros podem ser aplicados
   * Retorna false se houver campos inválidos ou se não houver nenhum filtro preenchido
   */
  get canApplyFilters(): boolean {
    // Verifica se o formulário é válido
    if (this.filterForm.invalid) {
      return false;
    }

    // Verifica se há pelo menos um filtro preenchido
    const values = this.filterForm.value;
    const hasAnyFilter = Object.values(values).some(
      (value) => value !== null && value !== '' && value !== undefined
    );

    return hasAnyFilter;
  }
}
